import type { AnchorHTMLAttributes, ComponentType, ReactNode } from "react";
import { LgPhrase } from "../../lg/LgPhrase";
import { SocialLinks } from "../layout/PageFooter/SocialLinks";

export type ErrorPageLinkComponent = ComponentType<
  AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }
>;

export const DefaultErrorPageLink: ErrorPageLinkComponent = ({
  to,
  children,
  ...props
}) => (
  <a href={to} {...props}>
    {children}
  </a>
);

export interface ErrorPageImage {
  src: string;
  width: number;
  height: number;
}

export interface ErrorPageDestination {
  path: string;
  title: string;
  description?: string;
  /** 行き先のページが共有画像として既に使っている絵 */
  image?: ErrorPageImage;
}

export function ErrorPageShell({
  code,
  mascot,
  artSrc,
  header,
  className = "",
  children,
}: {
  code: number;
  mascot: ReactNode;
  /** 場面の絵 (16 対 9)。code と同じ数字を絵の中に描いてあること。渡すと大きな数字と mascot の代わりに、横いっぱいで置く */
  artSrc?: string;
  header?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-main">
      {header}
      <main className={`flex-1 px-4 py-10 sm:py-16 ${className}`}>
        <div className="mx-auto flex w-full max-w-xl flex-col gap-8">
          {artSrc ? (
            /* 数字は絵の中に描いてある。読み上げと検索のために文字でも残す */
            <p className="sr-only">{code}</p>
          ) : (
            <div className="flex items-end justify-between gap-4">
              <p className="text-[88px] font-bold leading-none tracking-tight text-primary sm:text-[120px]">
                {code}
              </p>
              <div className="shrink-0 pb-2">{mascot}</div>
            </div>
          )}
          {artSrc ? (
            <img
              src={artSrc}
              alt=""
              width={1200}
              height={675}
              decoding="async"
              data-error-page-art
              className="scene-art aspect-[1200/675] h-auto w-full rounded-[2px] border border-main"
            />
          ) : null}
          {children}
        </div>
      </main>
    </div>
  );
}

/*
絵の枠は OGP と同じ 1.91 対 1 に揃える。16 対 9 の記事の絵も同じ枠に収め、
上下のわずかな余りを切る。行ごとに枠の形が違うと、字の頭が揃わない
*/
function DestinationThumbnail({ image }: { image: ErrorPageImage }) {
  return (
    <img
      src={image.src}
      alt=""
      width={image.width}
      height={image.height}
      loading="lazy"
      decoding="async"
      className="aspect-[1200/630] h-auto w-full object-cover"
    />
  );
}

export function DestinationList({
  heading,
  items,
  LinkComponent,
  listName,
}: {
  heading: string;
  items: readonly ErrorPageDestination[];
  LinkComponent: ErrorPageLinkComponent;
  listName: string;
}) {
  if (items.length === 0) return null;
  return (
    <section aria-label={heading} className="flex flex-col gap-3">
      <h2 className="max-sm:text-[0.9375rem] text-sm font-bold text-main">{heading}</h2>
      {/* 枠の外へ出す印は角の切り抜きに消されるので、行の印は内側に描く */}
      <ul className="divide-y divide-main overflow-hidden rounded-xl border border-main bg-surface">
        {items.map((item) => (
          <li key={item.path}>
            <LinkComponent
              to={item.path}
              data-error-page-list={listName}
              className="group flex items-center gap-4 px-4 py-3 transition-colors hover:bg-second focus-visible:bg-second focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            >
              {item.image && (
                <span className="w-24 shrink-0 overflow-hidden rounded-lg border border-main bg-second sm:w-32">
                  <DestinationThumbnail image={item.image} />
                </span>
              )}
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-bold text-main [word-break:auto-phrase] group-hover:text-primary">
                  {item.title}
                </span>
                {item.description && (
                  <span className="max-sm:text-[0.9375rem] text-sm leading-relaxed text-sub">
                    <LgPhrase>{item.description}</LgPhrase>
                  </span>
                )}
                <span className="truncate font-mono max-sm:text-[0.9375rem] text-sm text-sub">
                  {item.path}
                </span>
              </span>
              <span aria-hidden className="text-primary">
                →
              </span>
            </LinkComponent>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 記事は絵が主役なので、題を下に置いたカードで並べる */
export function ArticleCardList({
  heading,
  items,
  LinkComponent,
  listName,
}: {
  heading: string;
  items: readonly ErrorPageDestination[];
  LinkComponent: ErrorPageLinkComponent;
  listName: string;
}) {
  if (items.length === 0) return null;
  return (
    <section aria-label={heading} className="flex flex-col gap-3">
      <h2 className="max-sm:text-[0.9375rem] text-sm font-bold text-main">{heading}</h2>
      <ul className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <li key={item.path} className="min-w-0">
            <LinkComponent
              to={item.path}
              data-error-page-list={listName}
              className="group flex h-full flex-col overflow-hidden rounded-xl border border-main bg-surface transition-colors hover:bg-second focus-visible:bg-second focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {item.image && (
                <span className="block overflow-hidden border-b border-main bg-second">
                  <DestinationThumbnail image={item.image} />
                </span>
              )}
              {/* 余白は外の箱に持たせる。切った行が下の余白に覗くため */}
              <span className="px-3 py-2.5">
                <span className="block max-sm:text-[0.9375rem] text-sm font-bold leading-snug text-main [word-break:auto-phrase] [text-wrap:balance] group-hover:text-primary">
                  {item.title}
                </span>
              </span>
            </LinkComponent>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ErrorPageSocialLinks() {
  return (
    <section aria-label="公式SNS" className="flex flex-col gap-3">
      <h2 className="max-sm:text-[0.9375rem] text-sm font-bold text-main">公式SNS</h2>
      <SocialLinks />
    </section>
  );
}
